/**
 * Bonding curve math — one source of truth on the frontend.
 * MUST stay in lock-step with `backend/trading_engine.py::CURVE_EXPONENT`.
 *
 * Sqrt-family curve (concave):
 *   price(N)     = m × N^CURVE_EXPONENT
 *   revenue(N)   = ∫₀ᴺ m·x^exp dx = (m / (1+exp)) × N^(1+exp)
 *
 * Linear (legacy):
 *   price(N)     = m × N
 *   revenue(N)   = (m/2) × N²
 *
 * Feb 2026: `CURVE_EXPONENT` moved from 0.5 (pure sqrt) → 0.667 (2/3 power).
 * Rationale: at 0.5 the curve required ~4M Bits on the Balanced tier to
 * reach $500/Bit, which reads as absurd in the wizard preview. At 0.667
 * the same target lands at ~100K Bits — motivating and believable.
 * Opening prices unchanged (0.10 / 0.25 / 0.50 / 1.00 pts).
 */

import { isSqrtClan } from './clan';

export const CURVE_EXPONENT = 0.667;

/** Price of the Nth Bit for a sqrt-family curve with multiplier m. */
export const sqrtPriceAt = (m, n) => m * Math.pow(Math.max(1, n), CURVE_EXPONENT);

/** Cumulative primary-sale revenue from Bit 1 to N, sqrt-family. */
export const sqrtRevenueTo = (m, n) =>
  (m / (1 + CURVE_EXPONENT)) * Math.pow(Math.max(0, n), 1 + CURVE_EXPONENT);

/**
 * Discrete buy subtotal — sum of prices for the next `q` Bits starting
 * after `bitsMinted`. Uses the closed-form integral when q is large
 * enough that per-Bit rounding drift is negligible, otherwise sums
 * point-wise (matches the backend's per-Bit rounding exactly).
 */
export function sqrtBuySubtotal(m, bitsMinted, q) {
  if (q <= 0) return 0;
  // Small qtys → exact per-Bit sum (matches backend rounding).
  if (q <= 32) {
    let s = 0;
    for (let i = 1; i <= q; i += 1) s += sqrtPriceAt(m, bitsMinted + i);
    return s;
  }
  // Large qtys → integral approximation (indistinguishable to a user).
  return sqrtRevenueTo(m, bitsMinted + q) - sqrtRevenueTo(m, bitsMinted);
}

/** Discrete sell subtotal — mirror of `sqrtBuySubtotal`. */
export function sqrtSellSubtotal(m, bitsMinted, q) {
  if (q <= 0 || bitsMinted <= 0) return 0;
  if (q <= 32) {
    let s = 0;
    for (let i = 0; i < q; i += 1) s += sqrtPriceAt(m, Math.max(0, bitsMinted - i));
    return s;
  }
  return sqrtRevenueTo(m, bitsMinted) - sqrtRevenueTo(m, Math.max(0, bitsMinted - q));
}


/**
 * Single source of truth for a "what will this trade cost / earn" quote.
 * Callers get a consistent {subtotal, fee, total, oversold} shape whether
 * the clan is sqrt-family or legacy linear.
 *
 * - `clan`  → any object with { multiplier, bits_minted, curve_type? } —
 *             the same shape used across the app. `curve_type === 'linear'`
 *             OR a legacy numeric `supply_cap` marks a linear clan; anything
 *             else is treated as sqrt-family. Kept loose so callers can pass
 *             `creator.clan` directly.
 * - `qty`   → integer Bits (float will be floored by the price math anyway).
 * - `mode`  → 'buy' | 'sell'.
 * - `feeRate` → user's effective fee rate (0.10 for referred, 0.11 for solo).
 *
 * Returns `{ subtotal, fee, total, oversold }`. `total` is:
 *   - buy:  subtotal + fee   (what leaves the wallet)
 *   - sell: subtotal − fee   (what lands in the wallet)
 * `oversold` fires when a sell qty exceeds the clan's minted supply.
 *
 * Used by both the display quote (`TradingSheet` useMemo) and the retry
 * loop (`TradingSheet::onConfirm`) so slippage bounds and on-screen numbers
 * stay in lock-step even if one side is refactored later.
 */
export function computeTradeQuote(clan, qty, mode, feeRate) {
  if (!clan || !(qty > 0)) {
    return { subtotal: 0, fee: 0, total: 0, oversold: false };
  }
  const m = clan.multiplier;
  const n = clan.bits_minted || 0;
  const sqrtMode = isSqrtClan(clan);
  let subtotal = 0;
  let oversold = false;
  if (mode === 'buy') {
    subtotal = sqrtMode
      ? sqrtBuySubtotal(m, n, qty)
      : (m * qty * (2 * n + qty + 1)) / 2;
  } else {
    if (qty > n) {
      oversold = true;
    } else {
      subtotal = sqrtMode
        ? sqrtSellSubtotal(m, n, qty)
        : (m * qty * (2 * n - qty + 1)) / 2;
    }
  }
  const fee = subtotal * feeRate;
  const total = mode === 'buy' ? subtotal + fee : subtotal - fee;
  return { subtotal, fee, total, oversold };
}
